import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

import {
  // CircularProgress,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Grid,
  Box,
  ListItem,
  ListItemText,
} from "@mui/material";
import MDButton from "components/MDButton";

import { useReactMediaRecorder } from "react-media-recorder";
import { isMobile } from "react-device-detect";

function VideoPreview({ stream, mirrored }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
  if (!stream) {
    return null;
  }
  return (
    <video
      ref={videoRef}
      width="100%"
      autoPlay
      controls
      style={{ transform: !mirrored ? "rotateY(180deg)" : "rotateY(0deg)" }}
    >
      <track default kind="captions" srcLang="en" />
    </video>
  );
}

function VideoRecorder({
  formRef,
  draftIndex,
  displayName,
  fieldRef,
  options,
}) {
  console.log(options ? "" : "");

  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [recordedVideo, setRecordedVideo] = useState(null);
  const [openRecordDialog, setOpenRecordDialog] = useState(false);

  const [cameraUsed, setCameraUsed] = useState(isMobile);
  const videoConstraints = {
    facingMode: !cameraUsed ? "user" : { exact: "environment" },
    // facingMode: "user",
    // facingMode: { exact: "environment" },
  };

  function saveMediaBlob(blobUrl, blob) {
    // console.log(blobUrl);
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      const base64data = reader.result;
      // console.log(base64data);
      setRecordedVideo(base64data);
    };
  }

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl,
  } = useReactMediaRecorder({
    video: videoConstraints,
    onStop: (blobUrl, blob) => {
      saveMediaBlob(blobUrl, blob);
    },
  });

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox mt={3}>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <MDBox>
            <video
              src={`data:audio/mp3;base64,${draftValue}`}
              width="50%"
              controls
            >
              <track
                default
                kind="captions"
                srcLang="en"
                src={`data:audio/mp3;base64,${draftValue}`}
              />
            </video>
          </MDBox>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
          </Snackbar>
          <Dialog open={openRecordDialog} fullWidth maxWidth="sm">
            <DialogTitle>
              <MDBox>Record Video</MDBox>
            </DialogTitle>
            <DialogContent>
              <MDBox mt={2} mb={3} align="center">
                <MDBox>
                  <Box mt={1}>
                    {status === "recording" && (
                      <VideoPreview
                        stream={previewStream}
                        mirrored={cameraUsed ? true : false}
                      />
                    )}
                  </Box>
                  <Box mt={1}>
                    <MDButton
                      size="small"
                      circular
                      variant="contained"
                      color={status === "recording" ? "error" : "success"}
                      onClick={
                        status === "recording" ? stopRecording : startRecording
                      }
                      startIcon={
                        <Icon>
                          {status === "recording"
                            ? "stop_circle"
                            : "play_circle"}
                        </Icon>
                      }
                    >
                      {status === "recording" ? "Stop" : "Start"} Recording
                    </MDButton>
                  </Box>
                </MDBox>
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setOpenRecordDialog(false);
                }}
                color="dark"
                iconOnly
                circular
                variant="outlined"
              >
                <Icon>close</Icon>
              </MDButton>
              &nbsp;
              <MDButton
                onClick={() => {
                  clearBlobUrl();
                  setRecordedVideo(null);
                }}
                color="error"
                iconOnly
                circular
                variant="outlined"
              >
                <Icon>delete</Icon>
              </MDButton>
              <MDButton
                color="success"
                variant="outlined"
                iconOnly
                circular
                onClick={() => {
                  if (status === "recording") {
                    stopRecording();
                    setOpenRecordDialog(false);
                    return;
                  }

                  if (recordedVideo === null) {
                    setErrorMsg("Video recording failed");
                    showFieldError(true);
                    return;
                  }
                  setOpenRecordDialog(false);
                }}
              >
                <Icon>save</Icon>
              </MDButton>
            </DialogActions>
          </Dialog>
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={8} md={8} lg={8}>
                <MDTypography
                  variant="h6"
                  fontWeight="light"
                  fontSize="small"
                  mb={2}
                >
                  {displayName}
                </MDTypography>
              </Grid>
              <Grid item xs={4} md={4} lg={4}>
                <MDBox align="right">
                  <MDButton
                    iconOnly
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOpenRecordDialog(true);
                    }}
                  >
                    <Icon>video_camera_back</Icon>
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={1}>
            {mediaBlobUrl && (
              <video src={mediaBlobUrl} width="50%" controls>
                <track
                  default
                  kind="captions"
                  srcLang="en"
                  src={mediaBlobUrl}
                />
              </video>
            )}
          </MDBox>
          <input
            id={`dff_${fieldRef}`}
            type="hidden"
            name={`${fieldRef}`}
            value={recordedVideo}
          />
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props of the Transaction
VideoRecorder.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

VideoRecorder.defaultProps = {
  options: null,
};

VideoPreview.propTypes = {
  stream: PropTypes.string,
};

VideoPreview.defaultProps = {
  stream: null,
};

export default VideoRecorder;
